import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Activity, 
  CheckCircle, 
  Percent, 
  Clock, 
  TrendingUp,
  AlertCircle
} from 'lucide-react';

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchKpis = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await fetch('/api/admin/dashboard/kpis', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Impossible de charger les KPIs du tableau de bord');
        }

        const data = await response.json();
        setKpis(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchKpis();
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchKpis, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <div style={styles.loadingContainer}>Chargement du tableau de bord...</div>;
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <AlertCircle size={32} color="var(--danger)" />
        <p style={{ marginTop: '12px' }}>{error}</p>
      </div>
    );
  }

  const kpiItems = [
    { 
      title: 'Chauffeurs en Ligne', 
      value: kpis?.onlineDrivers ?? 0, 
      icon: Users, 
      color: '#3b82f6', 
      glow: 'rgba(59, 130, 246, 0.15)',
      description: 'Chauffeurs connectés sur l\'application'
    },
    { 
      title: 'Courses Actives', 
      value: kpis?.activeRides ?? 0, 
      icon: Activity, 
      color: '#06b6d4', 
      glow: 'rgba(6, 182, 212, 0.15)',
      description: 'Courses en recherche ou en cours'
    },
    { 
      title: 'Courses Terminées', 
      value: kpis?.completedRides ?? 0, 
      icon: CheckCircle, 
      color: '#10b981', 
      glow: 'rgba(16, 185, 129, 0.15)',
      description: 'Volume total cumulé aujourd\'hui'
    },
    { 
      title: 'Taux d\'Acceptation', 
      value: `${kpis?.acceptanceRate ?? 100}%`, 
      icon: Percent, 
      color: '#f59e0b', 
      glow: 'rgba(245, 158, 11, 0.15)',
      description: 'Taux d\'acceptation moyen des offres'
    }
  ];

  // SLA Calculation variables
  const slaValidationVal = kpis?.slaValidationHours ?? 12.0;
  const slaPercent = Math.min(100, Math.max(5, Math.round((slaValidationVal * 100) / 24)));
  const slaColor = slaValidationVal < 24 ? 'var(--success)' : 'var(--danger)';
  const slaStatusText = slaValidationVal < 24 ? 'SLA respecté' : 'Hors SLA';

  // Average Wait Time calculation variables
  const avgWaitVal = kpis?.averageWaitTimeMinutes ?? 5.2;
  const waitPercent = Math.min(100, Math.max(5, Math.round((avgWaitVal * 100) / 10)));
  const waitColor = avgWaitVal < 7 ? 'var(--info)' : avgWaitVal < 10 ? 'var(--warning)' : 'var(--danger)';

  // Chart calculation variables
  const hourlyData = kpis?.hourlyRides && kpis.hourlyRides.length === 5 ? kpis.hourlyRides : [0, 0, 0, 0, 0];
  const maxVal = Math.max(10, ...hourlyData);
  const xCoords = [40, 160, 280, 400, 480];
  const yCoords = hourlyData.map(val => 180 - (val * 140 / maxVal));

  const linePath = `M ${xCoords[0]} ${yCoords[0]} ` + 
                   `L ${xCoords[1]} ${yCoords[1]} ` + 
                   `L ${xCoords[2]} ${yCoords[2]} ` + 
                   `L ${xCoords[3]} ${yCoords[3]} ` + 
                   `L ${xCoords[4]} ${yCoords[4]}`;

  const fillPath = `${linePath} L 480 180 L 40 180 Z`;

  const labelTop = maxVal;
  const labelMidTop = Math.round(maxVal * 0.5);
  const labelMidBottom = Math.round(maxVal * 0.125);

  return (
    <div style={styles.container}>
      {/* Overview Cards Grid */}
      <div style={styles.kpiGrid}>
        {kpiItems.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={idx} style={styles.kpiCard} className="glass-card">
              <div style={styles.kpiHeader}>
                <div style={{
                  ...styles.iconContainer,
                  backgroundColor: item.glow,
                  borderColor: `rgba(${parseInt(item.color.slice(1,3),16)}, ${parseInt(item.color.slice(3,5),16)}, ${parseInt(item.color.slice(5,7),16)}, 0.3)`
                }}>
                  <Icon size={20} color={item.color} />
                </div>
                <span style={styles.kpiTitle}>{item.title}</span>
              </div>
              <div style={styles.kpiBody}>
                <span style={styles.kpiValue}>{item.value}</span>
                <span style={styles.kpiDesc}>{item.description}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Dashboard Panel */}
      <div style={styles.panelGrid}>
        <div style={{ ...styles.panel, flex: 2 }} className="glass-card">
          <div style={styles.panelHeader}>
            <TrendingUp size={20} color="var(--primary)" />
            <h3 style={styles.panelTitle}>Performance Opérationnelle (Aujourd'hui)</h3>
          </div>
          <div style={styles.panelContent}>
            {/* Visual SVG chart representation */}
            <div style={styles.chartWrapper}>
              <svg viewBox="0 0 500 200" style={styles.chartSvg}>
                <defs>
                  <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.0" />
                  </linearGradient>
                </defs>
                {/* Grid Lines */}
                <line x1="40" y1="20" x2="480" y2="20" stroke="rgba(255,255,255,0.05)" />
                <line x1="40" y1="80" x2="480" y2="80" stroke="rgba(255,255,255,0.05)" />
                <line x1="40" y1="140" x2="480" y2="140" stroke="rgba(255,255,255,0.05)" />
                <line x1="40" y1="180" x2="480" y2="180" stroke="rgba(255,255,255,0.1)" />

                {/* Graph Path */}
                <path 
                  d={fillPath} 
                  fill="url(#chartGrad)" 
                />
                <path 
                  d={linePath} 
                  fill="none" 
                  stroke="var(--primary)" 
                  strokeWidth="3" 
                  strokeLinecap="round"
                />

                {/* Circles on dots with value label */}
                {hourlyData.map((val, idx) => (
                  <g key={idx}>
                    <circle 
                      cx={xCoords[idx]} 
                      cy={yCoords[idx]} 
                      r="4" 
                      fill="var(--primary)" 
                      stroke="white" 
                      strokeWidth="1.5" 
                    />
                    <text 
                      x={xCoords[idx]} 
                      y={yCoords[idx] - 8} 
                      fill="var(--text-primary)" 
                      fontSize="9" 
                      fontWeight="600"
                      textAnchor="middle"
                    >
                      {val}
                    </text>
                  </g>
                ))}

                {/* Labels */}
                <text x="15" y="24" fill="var(--text-muted)" fontSize="10">{labelTop}</text>
                <text x="15" y="84" fill="var(--text-muted)" fontSize="10">{labelMidTop}</text>
                <text x="15" y="144" fill="var(--text-muted)" fontSize="10">{labelMidBottom}</text>
                <text x="15" y="184" fill="var(--text-muted)" fontSize="10">0</text>

                <text x="40" y="196" fill="var(--text-muted)" fontSize="10">08h</text>
                <text x="160" y="196" fill="var(--text-muted)" fontSize="10">11h</text>
                <text x="280" y="196" fill="var(--text-muted)" fontSize="10">14h</text>
                <text x="400" y="196" fill="var(--text-muted)" fontSize="10">17h</text>
                <text x="470" y="196" fill="var(--text-muted)" fontSize="10">20h</text>
              </svg>
            </div>
            <div style={styles.chartLegend}>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendDot, backgroundColor: 'var(--primary)' }} />
                <span>Volume de demandes de courses / heure</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ ...styles.panel, flex: 1 }} className="glass-card">
          <div style={styles.panelHeader}>
            <Clock size={20} color="var(--warning)" />
            <h3 style={styles.panelTitle}>SLA & Temps d'attente</h3>
          </div>
          <div style={styles.panelContent}>
            <div style={styles.slaWidget}>
              <div style={styles.slaMetric}>
                <span style={styles.slaLabel}>Temps de validation SLA</span>
                <span style={styles.slaValue}>{slaValidationVal.toFixed(1)} heures</span>
                <div style={styles.progressBarWrapper}>
                  <div style={{ ...styles.progressBar, width: `${slaPercent}%`, backgroundColor: slaColor }} />
                </div>
                <span style={styles.slaSub}>Cible : &lt; 24 heures ({slaStatusText})</span>
              </div>

              <div style={{ ...styles.slaMetric, marginTop: '24px' }}>
                <span style={styles.slaLabel}>Temps d'attente client moyen</span>
                <span style={styles.slaValue}>{avgWaitVal.toFixed(1)} min</span>
                <div style={styles.progressBarWrapper}>
                  <div style={{ ...styles.progressBar, width: `${waitPercent}%`, backgroundColor: waitColor }} />
                </div>
                <span style={styles.slaSub}>Depuis l'acceptation du chauffeur</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    animation: 'fadeIn 0.4s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
    fontSize: '1.1rem',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '20px',
  },
  kpiCard: {
    padding: '24px',
  },
  kpiHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '38px',
    height: '38px',
    borderRadius: '10px',
    border: '1px solid transparent',
  },
  kpiTitle: {
    fontFamily: 'var(--font-title)',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'var(--text-secondary)',
  },
  kpiBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  kpiValue: {
    fontSize: '2rem',
    fontWeight: '700',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-title)',
  },
  kpiDesc: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
  },
  panelGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  },
  panel: {
    padding: '24px',
    minWidth: '300px',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '16px',
  },
  panelTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  panelContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  chartWrapper: {
    width: '100%',
    margin: '10px 0',
  },
  chartSvg: {
    width: '100%',
    height: 'auto',
  },
  chartLegend: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  slaWidget: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
  },
  slaMetric: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  slaLabel: {
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    fontWeight: '500',
  },
  slaValue: {
    fontSize: '1.4rem',
    fontWeight: '700',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-title)',
  },
  progressBarWrapper: {
    width: '100%',
    height: '6px',
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '9999px',
    overflow: 'hidden',
    margin: '4px 0',
  },
  progressBar: {
    height: '100%',
    borderRadius: '9999px',
  },
  slaSub: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
  }
};
